import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// OTO-28: single source of truth for the notation panel's adjustable height.
// Clamp rationale:
// - MIN 120px: below ~120px one staff plus NotationDisplay's zoom-control
//   overlay no longer fits; the panel becomes a useless sliver.
// - MAX 500px: with the compact keyboard (90px) and controls bar (52px), a
//   500px panel still leaves >= 300px of waterfall (its existing fallback
//   floor in PracticeView) on any container >= 942px tall. Smaller
//   containers are additionally capped at drag time by maxFromLayout in
//   useNotationPanelResize.
export const NOTATION_PANEL_MIN_HEIGHT = 120;
export const NOTATION_PANEL_MAX_HEIGHT = 500;
export const NOTATION_PANEL_DEFAULT_HEIGHT = 200; // pre-OTO-28 fixed NOTATION_HEIGHT

export function clampNotationPanelHeight(height: number): number {
  return Math.min(
    NOTATION_PANEL_MAX_HEIGHT,
    Math.max(NOTATION_PANEL_MIN_HEIGHT, Math.round(height))
  );
}

interface UIState {
  /** Persisted height (px) of the practice-view notation panel. */
  notationPanelHeight: number;
  setNotationPanelHeight: (height: number) => void;
}

// Follows the scoreStore persist precedent (name + partialize). Only the
// height is persisted; the panel's open/closed toggle stays ephemeral by
// design (user decision #3).
export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      notationPanelHeight: NOTATION_PANEL_DEFAULT_HEIGHT,
      setNotationPanelHeight: (height) =>
        set({ notationPanelHeight: clampNotationPanelHeight(height) }),
    }),
    {
      name: 'otoflow-ui',
      partialize: (state) => ({ notationPanelHeight: state.notationPanelHeight }),
    }
  )
);
